// =====================================================================
// BulkSelectionBar — the floating multi-select bar on the Documents table
// =====================================================================
// Mounts when ≥1 file is checked and floats at the bottom of the viewport, so
// the table toolbar (search / group by) never moves. Layout, left → right:
//
//   [n selected ▾]  Status ▾  Type ▾  Link to ▾  Validity ▾ | Edit all | ⤓ ⋯ ✕
//
// - Status / Type / Link apply on click; Validity has its own Apply (it needs
//   input first). Every menu shows what the selection has NOW ("2 of 5 now").
// - Every change goes through `run()`, which snapshots the old values first so
//   the toast's Undo can put them back (a second request with the snapshot —
//   never a computed inverse).
// - Edit all is the one place every field lives; only changed fields apply.
// - Shortcuts (selection only, focus not in a field, no dialog open):
//   S / T / L / V open the menus, E opens Edit all, Esc clears the selection.
//
// Data notes:
// - Links are stored INVERSELY: each borrower / property / entity carries
//   `associations.loanDocuments` (see components/associations/
//   DocumentAssociations.js). Linking uses associate_document_with_objects;
//   unlinking (and undoing either) rewrites that one target's list.
// - "Doesn't expire" is not offered: validity windows come from the doc-type
//   rules (constants/documents/docTypeExpirationDurations.js), and a hard-window
//   type always uses its business number — there is no field that turns
//   expiry off.
import AccountBalanceOutlined from "@mui/icons-material/AccountBalanceOutlined";
import AutoAwesomeOutlined from "@mui/icons-material/AutoAwesomeOutlined";
import CheckRounded from "@mui/icons-material/CheckRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import DownloadOutlined from "@mui/icons-material/DownloadOutlined";
import EventOutlined from "@mui/icons-material/EventOutlined";
import HorizontalRuleRounded from "@mui/icons-material/HorizontalRuleRounded";
import KeyboardArrowDownRounded from "@mui/icons-material/KeyboardArrowDownRounded";
import LinkRounded from "@mui/icons-material/LinkRounded";
import LocalOfferOutlined from "@mui/icons-material/LocalOfferOutlined";
import MoreHorizRounded from "@mui/icons-material/MoreHorizRounded";
import TaskAltOutlined from "@mui/icons-material/TaskAltOutlined";
import TuneRounded from "@mui/icons-material/TuneRounded";
import VisibilityOffOutlined from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import Popover from "@mui/material/Popover";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import { addressLabel } from "../../../../_src_shared/utils/functions/addressLabel";
import { formatDate } from "../../../../_src_shared/utils/functions/formatDate";
import {
  FormDateField,
  FormField,
  FormSegmented,
  FormSelectField,
  FormTextField,
} from "../../../../components/forms/FormPrimitives";
import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import {
  computeExpirationDate,
  isHardDurationDocType,
} from "../../../../constants/documents/docTypeExpirationDurations";
import {
  documentStatusLabel,
  selectOptionsDocumentStatus,
} from "../../../../constants/selectOptions/selectOptionsDocumentStatus";
import { useEntitiesHook } from "../../../../hooks/api/useEntitiesHook";
import { useFileDocGroupAndTypesHook } from "../../../../hooks/useFileDocGroupAndTypesHook";
import { useLoanFilesHook } from "../../../../hooks/useLoanFilesHook";
import { useUnderwritingHook } from "../../../../hooks/useUnderwritingHook";
import { loanDrilldownSet } from "../../../../redux/actions/loanDrilldownActions";
import { ink, slate, teal } from "../../../../theme/colors";
import { FORM, formSx } from "../../../../theme/formStyles";
import { isShareFlagOn } from "../../../../utils/documents/documentShareFlags";
import { isOcrolusSupportedDocument } from "../../../../utils/documents/ocrolusFileValidation";
import { getDocExpiration } from "./DocExpirationCell";
import { DOC_STATUS_TONES } from "./DocumentInsightCards";

// ── helpers ────────────────────────────────────────────────────────────

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

// Status options in the bar's order (the review pipeline, then the exception
// statuses) — values are the canonical backend strings, labels the display.
const STATUS_OPTIONS = DOC_STATUS_TONES.filter((t) =>
  selectOptionsDocumentStatus.includes(t.key),
);

// Wrap a callback-style API hook in a promise. Resolves `{ ok, data }` once —
// on success, on failure, or (for a hook that bails early without calling
// either) when the hook's own promise settles.
const callHook = (fn, args) =>
  new Promise((resolve) => {
    let done = false;
    const finish = (r) => {
      if (done) return;
      done = true;
      resolve(r);
    };
    Promise.resolve(
      fn({
        ...args,
        onSuccessFn: (data) => finish({ ok: true, data }),
        onFailFn: () => finish({ ok: false }),
      }),
    ).then(
      () => finish({ ok: false }),
      () => finish({ ok: false }),
    );
  });

// "All 5 files: X" or "Mixed: A (3), B (2)".
const summarize = (values, label = (x) => x) => {
  const counts = new Map();
  values.forEach((v) => counts.set(v, (counts.get(v) || 0) + 1));
  const entries = [...counts.entries()];
  if (entries.length === 1) {
    return `All ${plural(values.length, "file")}: ${label(entries[0][0])}`;
  }
  return `Mixed: ${entries
    .sort((a, b) => b[1] - a[1])
    .map(([k, c]) => `${label(k)} (${c})`)
    .join(", ")}`;
};

const validityStateOf = (doc) => {
  const exp = getDocExpiration(doc);
  if (!exp) return "Doesn't expire";
  if (exp.noStart) return "Not started";
  return exp.kind === "expired" ? "Expired" : "Active";
};

// Bank statements Ocrolus can take: PDF (the send hook's own gate) and filed
// as a statement / under Liquidity.
const isOcrolusEligible = (doc) =>
  isOcrolusSupportedDocument(doc) &&
  (doc?.docGroup === "Liquidity" || /statement/i.test(doc?.docType || ""));

// Link targets on the loan, by kind. Entities follow DocumentAssociations:
// the flat `entities[]` when present, else the legacy `borrowerEntity`.
const LINK_KINDS = [
  { value: "borrower", label: "Borrower", plural: "borrowers" },
  { value: "property", label: "Property", plural: "properties" },
  { value: "entity", label: "Entity", plural: "entities" },
];
const buildLinkTargets = (loan) => {
  const entityList = Array.isArray(loan?.entities)
    ? loan.entities
    : loan?.borrowerEntity
      ? [loan.borrowerEntity]
      : [];
  return {
    borrower: (loan?.borrowers || [])
      .filter((b) => b?._id)
      .map((b) => ({
        kind: "borrower",
        id: b._id,
        item: b,
        label:
          b.fullName ||
          [b.firstName, b.lastName].filter(Boolean).join(" ") ||
          "Unnamed borrower",
      })),
    property: (loan?.subjectProperties || [])
      .filter((p) => p?._id)
      .map((p) => ({
        kind: "property",
        id: p._id,
        item: p,
        label:
          addressLabel(p.address) ||
          p.address?.streetAddress ||
          "Unnamed property",
      })),
    entity: entityList
      .filter((e) => e?._id)
      .map((e) => ({
        kind: "entity",
        id: e._id,
        item: e,
        label: e.entityName || "Unnamed entity",
      })),
  };
};
const targetDocIds = (target) =>
  target?.item?.associations?.loanDocuments || [];

// Flags offered in Edit all. Hidden is last (it moves the row out of view).
const EDIT_ALL_FLAGS = [
  { field: "includeInThirdPartyReview", label: "Include in third-party review" },
  { field: "approvedForThirdParty", label: "Approved for third party" },
  { field: "shareWithCounterparty", label: "Share with counterparty" },
  { field: "excludeFromExport", label: "Exclude from export" },
  { field: "isHidden", label: "Hidden" },
];
const isFlagOn = (doc, field) =>
  field === "isHidden" || field === "excludeFromExport"
    ? doc?.[field] === true || doc?.[field] === "true" || doc?.[field] === "True"
    : isShareFlagOn(doc, field);

const VALIDITY_PRESETS = [30, 60, 90, 180];

// ── small presentational pieces ───────────────────────────────────────

const StatusPill = ({ status }) => {
  const tone = DOC_STATUS_TONES.find((t) => t.key === status);
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        height: 24,
        px: "9px",
        borderRadius: "6px",
        fontSize: 12.5,
        fontWeight: 500,
        whiteSpace: "nowrap",
        bgcolor: tone?.tint || slate[100],
        color: tone?.text || slate[600],
      }}
    >
      <Box
        component="span"
        sx={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          bgcolor: tone?.color || slate[400],
        }}
      />
      {documentStatusLabel(status)}
    </Box>
  );
};
StatusPill.propTypes = { status: PropTypes.string };

const Kbd = ({ children }) => (
  <Box
    component="kbd"
    sx={{
      fontFamily: "inherit",
      fontSize: 11,
      fontWeight: 500,
      lineHeight: 1,
      color: slate[400],
      border: `1px solid ${slate[300]}`,
      borderRadius: "4px",
      px: "5px",
      py: "3px",
      display: { xs: "none", sm: "inline-block" },
    }}
  >
    {children}
  </Box>
);
Kbd.propTypes = { children: PropTypes.node };

// "✓" when every selected file has it, "k of n now" when some do.
const CountMeta = ({ count, total, suffix = "now" }) => {
  if (!count) return null;
  if (count === total) {
    return <CheckRounded sx={{ fontSize: 16, color: teal[600] }} />;
  }
  return (
    <Txt sx={{ fontSize: 12, color: slate[400], whiteSpace: "nowrap" }}>
      {count} of {total} {suffix}
    </Txt>
  );
};
CountMeta.propTypes = {
  count: PropTypes.number,
  total: PropTypes.number,
  suffix: PropTypes.string,
};

const menuHeadSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1,
  px: "14px",
  pt: "12px",
  pb: "8px",
};
const menuTitleSx = { fontSize: 14, fontWeight: 600, color: ink };
const menuHintSx = {
  fontSize: 12,
  color: slate[400],
  px: "14px",
  pt: "2px",
  pb: "10px",
  display: "block",
};
const optionSx = {
  minHeight: 36,
  borderRadius: "8px",
  mx: "6px",
  px: "8px",
  gap: "10px",
  fontSize: 13.5,
  "&.Mui-focusVisible": { boxShadow: `inset 0 0 0 2px ${teal[600]}` },
};
const groupLabelSx = {
  fontSize: 12,
  color: slate[400],
  fontWeight: 500,
  px: "14px",
  pt: "10px",
  pb: "4px",
  display: "block",
};
const menuSearchSx = {
  px: "10px",
  pb: "6px",
  "& .MuiOutlinedInput-root": { height: 34, fontSize: 13.5, borderRadius: "8px" },
};

const MenuHead = ({ title, kbd }) => (
  <Box sx={menuHeadSx}>
    <Txt sx={menuTitleSx}>{title}</Txt>
    {kbd ? <Kbd>{kbd}</Kbd> : null}
  </Box>
);
MenuHead.propTypes = { title: PropTypes.node, kbd: PropTypes.node };

// From a menu's search box, ↓ moves into the option list and Enter picks the
// first match.
const searchKeyDown = (listRef, onPickFirst) => (e) => {
  if (e.key === "ArrowDown") {
    e.preventDefault();
    listRef.current?.querySelector('[role="menuitem"]:not(.Mui-disabled)')?.focus();
  }
  if (e.key === "Enter") {
    e.preventDefault();
    onPickFirst?.();
  }
};

// ── bar buttons ──
const barBtnSx = (active) => ({
  height: 36,
  minWidth: { xs: 38, md: 0 },
  px: { xs: 0, md: "11px" },
  border: 0,
  borderRadius: "8px",
  bgcolor: active ? "rgba(255,255,255,0.12)" : "transparent",
  color: "#fff",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "7px",
  fontFamily: "inherit",
  fontSize: 13.5,
  fontWeight: 500,
  whiteSpace: "nowrap",
  cursor: "pointer",
  transition: "background .12s",
  "&:hover": { bgcolor: "rgba(255,255,255,0.12)" },
  "&:focus-visible": { outline: `2px solid ${teal[300]}`, outlineOffset: 1 },
  "&:disabled": { opacity: 0.45, cursor: "not-allowed" },
  "& .MuiSvgIcon-root": { fontSize: 17 },
});
const lblSx = { display: { xs: "none", md: "inline" } };
const chevSx = {
  fontSize: "16px !important",
  color: "rgba(255,255,255,0.55)",
  ml: "-3px",
  display: { xs: "none", md: "inline-block" },
};
const Sep = () => (
  <Box
    sx={{
      width: "1px",
      height: 22,
      bgcolor: "rgba(255,255,255,0.18)",
      mx: "4px",
      flex: "none",
    }}
  />
);


const MenuButton = ({ id, icon, label, tip, active, disabled, onClick, buttonRef }) => (
  <Tooltip title={tip} placement="top" arrow disableInteractive>
    <Box
      component="button"
      type="button"
      ref={(el) => buttonRef(id, el)}
      aria-haspopup="true"
      aria-expanded={active}
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      sx={barBtnSx(active)}
    >
      {icon}
      <Box component="span" sx={lblSx}>
        {label}
      </Box>
      <KeyboardArrowDownRounded sx={chevSx} />
    </Box>
  </Tooltip>
);
MenuButton.propTypes = {
  id: PropTypes.string,
  icon: PropTypes.node,
  label: PropTypes.string,
  tip: PropTypes.string,
  active: PropTypes.bool,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  buttonRef: PropTypes.func,
};

// ── the bar ────────────────────────────────────────────────────────────

const BulkSelectionBar = ({
  loan,
  loanId,
  docs,
  shownCount,
  onSelectAllShown,
  onClearSelection,
  showSelectedOnly,
  onToggleShowSelectedOnly,
  onDownload,
  downloading,
  maxDownload,
  onExtract,
  onSendToOcrolus,
}) => {
  const dispatch = useDispatch();
  const { updateLoanDocMetadata, associateDocumentWithObjects } =
    useLoanFilesHook();
  const { updateBorrower, updateSubjectProperty } = useUnderwritingHook();
  const { updateEntityData } = useEntitiesHook();
  const { fileDocGroupAndTypes } = useFileDocGroupAndTypesHook();

  const n = docs.length;
  const open = n > 0;

  // ── menus ──
  const [menu, setMenu] = useState(null); // "count" | "status" | … | null
  const btnRefs = useRef({});
  const setBtnRef = useCallback((id, el) => {
    btnRefs.current[id] = el;
  }, []);
  const toggleMenu = useCallback(
    (key) => setMenu((cur) => (cur === key ? null : key)),
    [],
  );
  const closeMenu = useCallback(() => setMenu(null), []);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // ── toast ──
  const [toast, setToast] = useState(null); // { msg, undo, key }
  const toastTimer = useRef(null);
  const showToast = useCallback((msg, undo = null) => {
    clearTimeout(toastTimer.current);
    setToast({ msg, undo, key: Date.now() });
    toastTimer.current = setTimeout(() => setToast(null), undo ? 7000 : 3500);
  }, []);
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  const [busy, setBusy] = useState(false);

  const commitDeal = useCallback(
    (deal) => {
      if (deal && typeof deal === "object") dispatch(loanDrilldownSet(deal));
    },
    [dispatch],
  );

  const patchDocs = useCallback(
    async (rows) => {
      if (!rows.length) return true;
      const r = await callHook(updateLoanDocMetadata, { loanId, data: rows });
      if (r.ok) commitDeal(r.data);
      return r.ok;
    },
    [updateLoanDocMetadata, loanId, commitDeal],
  );

  // Rewrite one target's `associations.loanDocuments` (unlink + every undo).
  const setTargetDocIds = useCallback(
    async (target, ids) => {
      const data = {
        associations: { ...(target.item.associations || {}), loanDocuments: ids },
      };
      const r =
        target.kind === "borrower"
          ? await callHook(updateBorrower, { loanId, borrowerId: target.id, data })
          : target.kind === "property"
            ? await callHook(updateSubjectProperty, {
                loanId,
                propertyId: target.id,
                data,
              })
            : await callHook(updateEntityData, { loanId, entityId: target.id, data });
      if (r.ok) commitDeal(r.data);
      return r.ok;
    },
    [loanId, updateBorrower, updateSubjectProperty, updateEntityData, commitDeal],
  );

  /**
   * The one way a change is made. `metadata(doc)` returns the fields to write
   * on that doc (or null to skip it); `link` is `{ target, mode: "add" |
   * "remove" }`. Old values are captured first and become the Undo.
   */
  const run = useCallback(
    async ({ metadata, link, message }) => {
      if (!n || busy) return;
      setBusy(true);
      setMenu(null);

      let rows = [];
      let undoRows = [];
      if (metadata) {
        docs.forEach((d) => {
          const fields = metadata(d);
          if (!d?._id || !fields || !Object.keys(fields).length) return;
          rows.push({ _id: d._id, ...fields });
          const prev = { _id: d._id };
          Object.keys(fields).forEach((k) => {
            prev[k] = d[k] === undefined ? null : d[k];
          });
          undoRows.push(prev);
        });
      }

      let ok = await patchDocs(rows);

      let undoLink = null;
      if (ok && link) {
        const ids = docs.map((d) => d._id).filter(Boolean);
        const before = targetDocIds(link.target);
        if (link.mode === "add") {
          const r = await callHook(associateDocumentWithObjects, {
            dealId: loanId,
            loanDocumentsIds: ids,
            borrowerIds: link.target.kind === "borrower" ? [link.target.id] : [],
            subjectPropertyIds:
              link.target.kind === "property" ? [link.target.id] : [],
            entityIds: link.target.kind === "entity" ? [link.target.id] : [],
          });
          if (r.ok) commitDeal(r.data);
          ok = r.ok;
        } else {
          const drop = new Set(ids);
          ok = await setTargetDocIds(
            link.target,
            before.filter((id) => !drop.has(id)),
          );
        }
        undoLink = { target: link.target, ids: before };
      }

      setBusy(false);
      if (!ok) {
        // A metadata write that landed before a failed link still has an undo.
        if (undoRows.length) {
          showToast("Only part of the change was saved", async () => {
            await patchDocs(undoRows);
            showToast("Change undone");
          });
        } else {
          showToast("The change could not be saved");
        }
        return;
      }
      showToast(message, async () => {
        setBusy(true);
        const okMeta = await patchDocs(undoRows);
        const okLink = undoLink
          ? await setTargetDocIds(undoLink.target, undoLink.ids)
          : true;
        setBusy(false);
        showToast(okMeta && okLink ? "Change undone" : "Undo could not be saved");
      });
    },
    [
      n,
      busy,
      docs,
      patchDocs,
      associateDocumentWithObjects,
      loanId,
      commitDeal,
      setTargetDocIds,
      showToast,
    ],
  );

  // ── keyboard ──
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      // Any open MUI modal (our menus / drawer, or another dialog) handles its
      // own keys — Esc included.
      if (document.querySelector(".MuiModal-root:not(.MuiModal-hidden)")) return;
      if (e.key === "Escape") {
        onClearSelection();
        return;
      }
      const tag = e.target?.tagName || "";
      if (
        /INPUT|SELECT|TEXTAREA/.test(tag) ||
        e.target?.isContentEditable ||
        e.metaKey ||
        e.ctrlKey ||
        e.altKey
      )
        return;
      const map = { s: "status", t: "type", l: "link", v: "validity" };
      const k = e.key.toLowerCase();
      if (map[k]) {
        e.preventDefault();
        toggleMenu(map[k]);
      } else if (k === "e") {
        e.preventDefault();
        setDrawerOpen(true);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClearSelection, toggleMenu]);

  // Nothing selected → nothing for a menu or the panel to act on.
  useEffect(() => {
    if (!open) {
      setMenu(null);
      setDrawerOpen(false);
    }
  }, [open]);

  const linkTargets = useMemo(() => buildLinkTargets(loan), [loan]);


  const popProps = (id, width = 320) => ({
    open: menu === id,
    anchorEl: btnRefs.current[id] || null,
    onClose: closeMenu,
    anchorOrigin: { vertical: "top", horizontal: "left" },
    transformOrigin: { vertical: "bottom", horizontal: "left" },
    marginThreshold: 12,
    slotProps: {
      paper: {
        sx: {
          mt: "-10px",
          width: { xs: "calc(100% - 24px)", sm: width },
          maxHeight: "min(460px, calc(100vh - 140px))",
          display: "flex",
          flexDirection: "column",
          borderRadius: "12px",
          border: `1px solid ${slate[200]}`,
          boxShadow:
            "0 12px 32px rgba(16,24,32,.16), 0 2px 6px rgba(16,24,32,.08)",
        },
      },
    },
  });

  const tooManyToDownload = n > maxDownload;
  const ocrolusCount = docs.filter(isOcrolusEligible).length;
  const allHidden = n > 0 && docs.every((d) => isFlagOn(d, "isHidden"));

  return (
    <>
      {/* ── the bar ── */}
      <Box
        role="toolbar"
        aria-label="Actions for selected files"
        sx={{
          position: "fixed",
          left: "50%",
          bottom: "calc(20px + env(safe-area-inset-bottom, 0px))",
          transform: open ? "translate(-50%, 0)" : "translate(-50%, 24px)",
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          transition:
            "transform .2s cubic-bezier(.2,.8,.2,1), opacity .2s",
          zIndex: 1200,
          maxWidth: "calc(100% - 24px)",
          bgcolor: ink,
          color: "#fff",
          borderRadius: "12px",
          p: "6px",
          display: "flex",
          alignItems: "center",
          gap: "2px",
          boxShadow:
            "0 12px 32px rgba(16,24,32,.16), 0 2px 6px rgba(16,24,32,.08)",
          "@media (prefers-reduced-motion: reduce)": { transition: "none" },
        }}
      >
        <Box
          component="button"
          type="button"
          ref={(el) => {
            btnRefs.current.count = el;
          }}
          aria-haspopup="true"
          aria-expanded={menu === "count"}
          onClick={() => toggleMenu("count")}
          sx={{
            ...barBtnSx(menu === "count"),
            minWidth: 0,
            pl: "12px",
            pr: "10px",
            bgcolor:
              menu === "count" ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.1)",
          }}
        >
          {busy ? (
            <CircularProgress size={14} sx={{ color: "inherit" }} />
          ) : null}
          <span>
            <b style={{ fontWeight: 600 }}>{n}</b> selected
          </span>
          <KeyboardArrowDownRounded
            sx={{ ...chevSx, display: "inline-block" }}
          />
        </Box>
        <Sep />
        <MenuButton
          id="status"
          active={menu === "status"}
          disabled={busy}
          onClick={() => toggleMenu("status")}
          buttonRef={setBtnRef}
          icon={<TaskAltOutlined />}
          label="Status"
          tip="Status (S)"
        />
        <MenuButton
          id="type"
          active={menu === "type"}
          disabled={busy}
          onClick={() => toggleMenu("type")}
          buttonRef={setBtnRef}
          icon={<LocalOfferOutlined />}
          label="Type"
          tip="Type (T)"
        />
        <MenuButton
          id="link"
          active={menu === "link"}
          disabled={busy}
          onClick={() => toggleMenu("link")}
          buttonRef={setBtnRef}
          icon={<LinkRounded />}
          label="Link to"
          tip="Link to (L)"
        />
        <MenuButton
          id="validity"
          active={menu === "validity"}
          disabled={busy}
          onClick={() => toggleMenu("validity")}
          buttonRef={setBtnRef}
          icon={<EventOutlined />}
          label="Validity"
          tip="Validity (V)"
        />
        <Tooltip title="Edit all fields (E)" placement="top" arrow disableInteractive>
          <Box
            component="button"
            type="button"
            aria-label="Edit all"
            onClick={() => setDrawerOpen(true)}
            disabled={busy}
            sx={barBtnSx(drawerOpen)}
          >
            <TuneRounded />
            <Box component="span" sx={lblSx}>
              Edit all
            </Box>
          </Box>
        </Tooltip>
        <Sep />
        <Tooltip
          title={
            tooManyToDownload
              ? `Download is limited to ${maxDownload} files at a time. Use Download All for the whole loan.`
              : `Download ${plural(n, "file")}`
          }
          placement="top"
          arrow
          disableInteractive
        >
          <span>
            <Box
              component="button"
              type="button"
              aria-label={`Download ${plural(n, "file")}`}
              disabled={busy || downloading || tooManyToDownload}
              onClick={() => {
                showToast(`Downloading ${plural(n, "file")}`);
                onDownload();
              }}
              sx={{ ...barBtnSx(false), width: 36, minWidth: 36, px: 0 }}
            >
              {downloading ? (
                <CircularProgress size={15} sx={{ color: "inherit" }} />
              ) : (
                <DownloadOutlined />
              )}
            </Box>
          </span>
        </Tooltip>
        <Tooltip title="More actions" placement="top" arrow disableInteractive>
          <Box
            component="button"
            type="button"
            ref={(el) => {
              btnRefs.current.more = el;
            }}
            aria-haspopup="true"
            aria-expanded={menu === "more"}
            aria-label="More actions"
            disabled={busy}
            onClick={() => toggleMenu("more")}
            sx={{ ...barBtnSx(menu === "more"), width: 36, minWidth: 36, px: 0 }}
          >
            <MoreHorizRounded />
          </Box>
        </Tooltip>
        <Tooltip title="Clear selection (Esc)" placement="top" arrow disableInteractive>
          <Box
            component="button"
            type="button"
            aria-label="Clear selection"
            onClick={onClearSelection}
            sx={{ ...barBtnSx(false), width: 36, minWidth: 36, px: 0 }}
          >
            <CloseRounded />
          </Box>
        </Tooltip>
      </Box>

      {/* ── count menu ── */}
      <Popover {...popProps("count", 300)}>
        <MenuList autoFocusItem sx={{ py: "6px" }}>
          <MenuItem
            sx={optionSx}
            onClick={() => {
              onSelectAllShown();
              closeMenu();
            }}
          >
            <CheckRounded sx={{ fontSize: 17, color: teal[600] }} />
            <Box sx={{ flex: 1 }}>Select all {shownCount} shown</Box>
          </MenuItem>
          <MenuItem
            sx={optionSx}
            onClick={() => {
              closeMenu();
              onClearSelection();
            }}
          >
            <HorizontalRuleRounded sx={{ fontSize: 17, color: teal[600] }} />
            <Box sx={{ flex: 1 }}>Clear selection</Box>
            <Kbd>Esc</Kbd>
          </MenuItem>
          <Divider sx={{ my: "6px !important", mx: "4px" }} />
          <MenuItem
            sx={optionSx}
            onClick={() => onToggleShowSelectedOnly(!showSelectedOnly)}
          >
            <Checkbox
              size="small"
              checked={showSelectedOnly}
              tabIndex={-1}
              disableRipple
              sx={{ p: 0 }}
            />
            <Box sx={{ flex: 1 }}>Show selected only</Box>
          </MenuItem>
        </MenuList>
        <Divider />
        <Txt sx={{ ...menuHintSx, pt: "10px" }}>
          Shift-click a checkbox to select a range.
        </Txt>
      </Popover>

      {/* ── status menu ── */}
      <Popover {...popProps("status")}>
        <MenuHead title={`Set status for ${plural(n, "file")}`} kbd="S" />
        <MenuList autoFocusItem sx={{ py: "2px", overflow: "auto" }}>
          {STATUS_OPTIONS.map((s) => {
            const c = docs.filter((d) => d.documentStatus === s.key).length;
            return (
              <MenuItem
                key={s.key}
                sx={optionSx}
                onClick={() =>
                  run({
                    metadata: () => ({ documentStatus: s.key }),
                    message: `Set ${plural(n, "file")} to ${documentStatusLabel(
                      s.key,
                    ).toLowerCase()}`,
                  })
                }
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <StatusPill status={s.key} />
                </Box>
                <CountMeta count={c} total={n} />
              </MenuItem>
            );
          })}
        </MenuList>
        <Txt sx={menuHintSx}>Applies right away. You can undo.</Txt>
      </Popover>

      {/* ── type menu ── */}
      <TypeMenu
        popProps={popProps("type")}
        n={n}
        docs={docs}
        catalog={fileDocGroupAndTypes}
        onPick={(group, type) =>
          run({
            metadata: () => ({ docGroup: group, docType: type }),
            message: `Set ${plural(n, "file")} to ${type}`,
          })
        }
      />

      {/* ── link menu ── */}
      <LinkMenu
        popProps={popProps("link")}
        n={n}
        docs={docs}
        targets={linkTargets}
        onPick={(target, all) =>
          run({
            link: { target, mode: all ? "remove" : "add" },
            message: all
              ? `Unlinked ${plural(n, "file")} from ${target.label}`
              : `Linked ${plural(n, "file")} to ${target.label}`,
          })
        }
      />

      {/* ── validity menu ── */}
      <ValidityMenu
        popProps={popProps("validity")}
        n={n}
        docs={docs}
        onCancel={closeMenu}
        onApply={({ start, days }) =>
          run({
            metadata: () => {
              const f = {};
              if (start) f.reportEffectiveDate = String(start).slice(0, 10);
              if (days) f.reportGuidelineExpirationDays = days;
              return f;
            },
            message: `Updated validity on ${plural(n, "file")}`,
          })
        }
      />

      {/* ── more menu ── */}
      <Popover {...popProps("more", 300)}>
        <MenuList autoFocusItem sx={{ py: "6px" }}>
          <MenuItem
            sx={optionSx}
            onClick={() => {
              closeMenu();
              onExtract();
            }}
          >
            <AutoAwesomeOutlined sx={{ fontSize: 17, color: teal[600] }} />
            <Box sx={{ flex: 1 }}>Extract data</Box>
          </MenuItem>
          <Tooltip
            title={ocrolusCount ? "" : "Only PDF bank statements can be sent"}
            placement="left"
          >
            <span>
              <MenuItem
                sx={{ ...optionSx, alignItems: "flex-start", py: "8px" }}
                disabled={!ocrolusCount}
                onClick={() => {
                  closeMenu();
                  onSendToOcrolus(docs.filter(isOcrolusEligible));
                }}
              >
                <AccountBalanceOutlined
                  sx={{ fontSize: 17, color: teal[600], mt: "2px" }}
                />
                <Box sx={{ flex: 1 }}>
                  <Txt component="div" sx={{ fontSize: 13.5 }}>
                    Send to Ocrolus
                  </Txt>
                  <Txt component="div" sx={{ fontSize: 12, color: slate[400] }}>
                    {ocrolusCount
                      ? `${plural(ocrolusCount, "bank statement")} of ${n} selected`
                      : "Only PDF bank statements can be sent"}
                  </Txt>
                </Box>
              </MenuItem>
            </span>
          </Tooltip>
          <Divider sx={{ my: "6px !important", mx: "4px" }} />
          <MenuItem
            sx={optionSx}
            onClick={() =>
              run({
                metadata: () => ({ isHidden: !allHidden }),
                message: allHidden
                  ? `Unhid ${plural(n, "file")}`
                  : `Hid ${plural(n, "file")}`,
              })
            }
          >
            {allHidden ? (
              <VisibilityOutlined sx={{ fontSize: 17, color: teal[600] }} />
            ) : (
              <VisibilityOffOutlined sx={{ fontSize: 17, color: teal[600] }} />
            )}
            <Box sx={{ flex: 1 }}>{allHidden ? "Unhide" : "Hide from list"}</Box>
          </MenuItem>
        </MenuList>
      </Popover>

      {/* ── edit all panel ── */}
      <EditAllDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        docs={docs}
        catalog={fileDocGroupAndTypes}
        targets={linkTargets}
        busy={busy}
        onApply={({ metadata, link, count }) => {
          setDrawerOpen(false);
          run({
            metadata,
            link,
            message: `Updated ${plural(n, "file")} with ${plural(count, "change")}`,
          });
        }}
      />

      {/* ── toast ── */}
      <Box
        role="status"
        aria-live="polite"
        sx={{
          position: "fixed",
          left: "50%",
          bottom: open
            ? "calc(80px + env(safe-area-inset-bottom, 0px))"
            : "calc(20px + env(safe-area-inset-bottom, 0px))",
          transform: toast ? "translate(-50%, 0)" : "translate(-50%, 16px)",
          opacity: toast ? 1 : 0,
          pointerEvents: toast ? "auto" : "none",
          transition: "transform .2s, opacity .2s, bottom .2s",
          zIndex: 1250,
          maxWidth: "calc(100% - 24px)",
          bgcolor: ink,
          color: "#fff",
          borderRadius: "10px",
          pl: "16px",
          pr: "10px",
          py: "10px",
          display: "flex",
          alignItems: "center",
          gap: "14px",
          fontSize: 13.5,
          boxShadow:
            "0 12px 32px rgba(16,24,32,.16), 0 2px 6px rgba(16,24,32,.08)",
          "@media (prefers-reduced-motion: reduce)": { transition: "none" },
        }}
      >
        <span>{toast?.msg}</span>
        {toast?.undo ? (
          <Box
            component="button"
            type="button"
            onClick={() => {
              const undo = toast.undo;
              setToast(null);
              undo();
            }}
            sx={{
              border: 0,
              bgcolor: "transparent",
              color: "inherit",
              fontFamily: "inherit",
              fontWeight: 600,
              fontSize: 13.5,
              height: 30,
              px: "10px",
              borderRadius: "6px",
              cursor: "pointer",
              "&:hover": { bgcolor: "rgba(255,255,255,0.14)" },
            }}
          >
            Undo
          </Box>
        ) : null}
      </Box>
    </>
  );
};

BulkSelectionBar.propTypes = {
  loan: PropTypes.object,
  loanId: PropTypes.string,
  docs: PropTypes.array.isRequired,
  shownCount: PropTypes.number,
  onSelectAllShown: PropTypes.func.isRequired,
  onClearSelection: PropTypes.func.isRequired,
  showSelectedOnly: PropTypes.bool,
  onToggleShowSelectedOnly: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  downloading: PropTypes.bool,
  maxDownload: PropTypes.number,
  onExtract: PropTypes.func.isRequired,
  onSendToOcrolus: PropTypes.func.isRequired,
};

// ── Type menu ──────────────────────────────────────────────────────────

const TypeMenu = ({ popProps, n, docs, catalog, onPick }) => {
  const [q, setQ] = useState("");
  const listRef = useRef(null);
  useEffect(() => {
    if (!popProps.open) setQ("");
  }, [popProps.open]);

  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return Object.entries(catalog || {})
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([group, types]) => ({
        group,
        types: (types || []).filter(
          (t) =>
            !needle ||
            t.toLowerCase().includes(needle) ||
            group.toLowerCase().includes(needle),
        ),
      }))
      .filter((g) => g.types.length);
  }, [catalog, q]);

  const first = groups[0] ? [groups[0].group, groups[0].types[0]] : null;

  return (
    <Popover {...popProps}>
      <MenuHead title={`Set type for ${plural(n, "file")}`} kbd="T" />
      <TextField
        autoFocus
        size="small"
        placeholder="Search types or categories"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={searchKeyDown(listRef, () => first && onPick(...first))}
        inputProps={{ "aria-label": "Search types or categories" }}
        sx={menuSearchSx}
      />
      <Box ref={listRef} sx={{ overflow: "auto", pb: "6px" }}>
        {groups.length === 0 ? (
          <Txt sx={{ ...menuHintSx, py: "16px", fontSize: 13 }}>
            No types match &ldquo;{q}&rdquo;.
          </Txt>
        ) : (
          <MenuList sx={{ py: 0 }}>
            {groups.flatMap(({ group, types }) => [
              <Txt key={`h:${group}`} sx={groupLabelSx}>
                {group}
              </Txt>,
              ...types.map((type) => {
                const c = docs.filter(
                  (d) => d.docType === type && d.docGroup === group,
                ).length;
                return (
                  <MenuItem
                    key={`${group}|${type}`}
                    sx={optionSx}
                    onClick={() => onPick(group, type)}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>{type}</Box>
                    <CountMeta count={c} total={n} />
                  </MenuItem>
                );
              }),
            ])}
          </MenuList>
        )}
      </Box>
      <Divider />
      <Txt sx={{ ...menuHintSx, pt: "10px" }}>
        The category updates to match the type.
      </Txt>
    </Popover>
  );
};
TypeMenu.propTypes = {
  popProps: PropTypes.object,
  n: PropTypes.number,
  docs: PropTypes.array,
  catalog: PropTypes.object,
  onPick: PropTypes.func,
};

// ── Link menu ──────────────────────────────────────────────────────────

const LinkMenu = ({ popProps, n, docs, targets, onPick }) => {
  const [kind, setKind] = useState("property");
  const [q, setQ] = useState("");
  const listRef = useRef(null);
  useEffect(() => {
    if (!popProps.open) setQ("");
  }, [popProps.open]);

  const kindMeta = LINK_KINDS.find((k) => k.value === kind);
  const needle = q.trim().toLowerCase();
  const options = (targets[kind] || []).filter(
    (t) => !needle || t.label.toLowerCase().includes(needle),
  );
  const linkedCount = (t) => {
    const ids = new Set(targetDocIds(t));
    return docs.filter((d) => ids.has(d._id)).length;
  };

  return (
    <Popover {...popProps}>
      <MenuHead title={`Link ${plural(n, "file")} to`} kbd="L" />
      <Box sx={{ px: "10px", pb: "8px" }}>
        <FormSegmented
          ariaLabel="Link target type"
          value={kind}
          onChange={(v) => {
            if (!v) return;
            setKind(v);
            setQ("");
          }}
          options={LINK_KINDS.map((k) => ({ value: k.value, label: k.label }))}
        />
      </Box>
      <TextField
        autoFocus
        size="small"
        placeholder={`Search ${kindMeta.plural}`}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={searchKeyDown(listRef, () => {
          const t = options[0];
          if (t) onPick(t, linkedCount(t) === n);
        })}
        inputProps={{ "aria-label": `Search ${kindMeta.plural}` }}
        sx={menuSearchSx}
      />
      <Box ref={listRef} sx={{ overflow: "auto", pb: "6px" }}>
        {options.length === 0 ? (
          <Txt sx={{ ...menuHintSx, py: "16px", fontSize: 13 }}>
            {needle
              ? `No ${kindMeta.plural} match "${q}".`
              : `No ${kindMeta.plural} on this loan.`}
          </Txt>
        ) : (
          <MenuList sx={{ py: 0 }}>
            {options.map((t) => {
              const c = linkedCount(t);
              const all = c === n;
              return (
                <MenuItem
                  key={t.id}
                  sx={{ ...optionSx, whiteSpace: "normal" }}
                  onClick={() => onPick(t, all)}
                >
                  <Box sx={{ width: 18, display: "inline-flex", flex: "none" }}>
                    {all ? (
                      <CheckRounded sx={{ fontSize: 16, color: teal[600] }} />
                    ) : c ? (
                      <HorizontalRuleRounded
                        sx={{ fontSize: 16, color: teal[600] }}
                      />
                    ) : null}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>{t.label}</Box>
                  {c ? (
                    <Txt sx={{ fontSize: 12, color: slate[400], whiteSpace: "nowrap" }}>
                      {all ? "All linked" : `${c} of ${n} linked`}
                    </Txt>
                  ) : null}
                </MenuItem>
              );
            })}
          </MenuList>
        )}
      </Box>
      <Divider />
      <Txt sx={{ ...menuHintSx, pt: "10px" }}>
        Click a linked item again to unlink it.
      </Txt>
    </Popover>
  );
};
LinkMenu.propTypes = {
  popProps: PropTypes.object,
  n: PropTypes.number,
  docs: PropTypes.array,
  targets: PropTypes.object,
  onPick: PropTypes.func,
};

// ── Validity menu ──────────────────────────────────────────────────────

const fmtShort = (ymd) => formatDate(ymd, "", "M/d/yy");

// The live sentence under the inputs; returns null when there's nothing to do.
const validityPreview = ({ start, days, n }) => {
  const d = Number(days) || 0;
  if (start && d) {
    return `Valid ${fmtShort(start)} to ${fmtShort(
      computeExpirationDate(start, d),
    )}.`;
  }
  if (start) return `Starts ${fmtShort(start)}. Each file keeps its current duration.`;
  if (d) return `${d}-day window. Each file keeps its current start date.`;
  return null;
};

const ValidityMenu = ({ popProps, n, docs, onApply, onCancel }) => {
  const [start, setStart] = useState(null);
  const [days, setDays] = useState("");
  useEffect(() => {
    if (!popProps.open) {
      setStart(null);
      setDays("");
    }
  }, [popProps.open]);

  const numDays = Number(days) || 0;
  const preview = validityPreview({ start, days, n });
  // Hard-window types always use their business number (doc validity rules),
  // so a typed duration only reaches the others.
  const fixed = docs.filter((d) => isHardDurationDocType(d.docType)).length;

  return (
    <Popover {...popProps}>
      <MenuHead title={`Set validity for ${plural(n, "file")}`} kbd="V" />
      <Box sx={{ px: "14px", pb: "12px", display: "grid", gap: "12px", overflow: "auto" }}>
        <FormDateField label="Start date" value={start} onChange={setStart} />
        <FormField label="Valid for">
          <Flx column g={1}>
            <Flx g={0.75} sx={{ flexWrap: "wrap" }}>
              {VALIDITY_PRESETS.map((p) => {
                const on = numDays === p;
                return (
                  <Box
                    key={p}
                    component="button"
                    type="button"
                    aria-pressed={on}
                    onClick={() => setDays(on ? "" : String(p))}
                    sx={{
                      ...formSx.chip(on),
                      height: 28,
                      px: "10px",
                      borderRadius: "99px",
                      fontSize: 12.5,
                      cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    {p} days
                  </Box>
                );
              })}
            </Flx>
            <TextField
              size="small"
              type="number"
              placeholder="Or enter days"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              inputProps={{ min: 1, step: 1, "aria-label": "Days valid" }}
              sx={formSx.input}
            />
          </Flx>
        </FormField>
        <Box
          sx={{
            fontSize: 13,
            px: "10px",
            py: "8px",
            borderRadius: "8px",
            bgcolor: slate[50],
            color: slate[600],
          }}
        >
          {preview ||
            "Pick a start date, a duration, or both. Anything left blank keeps each file's current value."}
          {numDays && fixed ? (
            <Box component="span" sx={{ display: "block", mt: "4px", color: slate[500] }}>
              {fixed} of {n} use a fixed window for their type, so only the start
              date applies to them.
            </Box>
          ) : null}
        </Box>
      </Box>
      <Flx
        jc="flex-end"
        g={1}
        sx={{ px: "12px", py: "10px", borderTop: `1px solid ${slate[200]}` }}
      >
        <Button variant="text" color="gray" onClick={onCancel} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!start && !numDays}
          onClick={() => onApply({ start, days: numDays || null })}
          sx={{ textTransform: "none" }}
        >
          Apply to {plural(n, "file")}
        </Button>
      </Flx>
    </Popover>
  );
};
ValidityMenu.propTypes = {
  popProps: PropTypes.object,
  n: PropTypes.number,
  docs: PropTypes.array,
  onApply: PropTypes.func,
  onCancel: PropTypes.func,
};

// ── Edit all panel ─────────────────────────────────────────────────────

const EMPTY_DRAFT = {
  status: "",
  type: "", // "group|type"
  link: "", // "kind|id"
  vmode: "keep", // "keep" | "set"
  start: null,
  days: "",
  flags: {}, // field → true | false
};

// A field block: tinted + accent rule + Reset once changed.
const Fld = ({ title, now, changed, onReset, children }) => (
  <Box
    sx={{
      px: "22px",
      py: "14px",
      borderLeft: `3px solid ${changed ? FORM.color.accent : "transparent"}`,
      bgcolor: changed ? FORM.color.accentTint : "transparent",
      transition: "background .15s, border-color .15s",
    }}
  >
    <Flx ac jb g={1} sx={{ mb: "4px" }}>
      <Txt sx={formSx.sectionTitle}>{title}</Txt>
      {changed ? (
        <Box
          component="button"
          type="button"
          onClick={onReset}
          sx={{
            ...formSx.link,
            border: 0,
            bgcolor: "transparent",
            p: 0,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          Reset
        </Box>
      ) : null}
    </Flx>
    {now ? (
      <Txt component="div" sx={{ ...formSx.meta, mb: "8px" }}>
        {now}
      </Txt>
    ) : null}
    {children}
  </Box>
);
Fld.propTypes = {
  title: PropTypes.node,
  now: PropTypes.node,
  changed: PropTypes.bool,
  onReset: PropTypes.func,
  children: PropTypes.node,
};

const EditAllDrawer = ({ open, onClose, docs, catalog, targets, busy, onApply }) => {
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  useEffect(() => {
    if (open) setDraft(EMPTY_DRAFT);
  }, [open]);
  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const n = docs.length;

  const typeOptions = useMemo(
    () =>
      Object.entries(catalog || {})
        .sort(([a], [b]) => a.localeCompare(b))
        .flatMap(([group, types]) =>
          (types || []).map((t) => ({
            value: `${group}|${t}`,
            label: `${t} (${group})`,
          })),
        ),
    [catalog],
  );
  const linkOptions = useMemo(
    () =>
      LINK_KINDS.flatMap((k) =>
        (targets[k.value] || []).map((t) => ({
          value: `${k.value}|${t.id}`,
          label: `${k.label}: ${t.label}`,
        })),
      ),
    [targets],
  );
  const findTarget = (key) => {
    if (!key) return null;
    const i = key.indexOf("|");
    const kind = key.slice(0, i);
    const id = key.slice(i + 1);
    return (targets[kind] || []).find((t) => String(t.id) === id) || null;
  };

  // What the selection has now.
  const nowStatus = summarize(
    docs.map((d) => d.documentStatus || "No status"),
    documentStatusLabel,
  );
  const nowType = summarize(docs.map((d) => d.docType || "No type"));
  const linkedNow = LINK_KINDS.flatMap((k) =>
    (targets[k.value] || [])
      .map((t) => {
        const ids = new Set(targetDocIds(t));
        const c = docs.filter((d) => ids.has(d._id)).length;
        return c ? `${t.label} (${c})` : null;
      })
      .filter(Boolean),
  );
  const nowValidity = summarize(docs.map(validityStateOf));

  // Changes — each moved flag counts as one.
  const flagChanges = Object.keys(draft.flags).length;
  const validityChanged =
    draft.vmode === "set" && (!!draft.start || Number(draft.days) > 0);
  const count =
    (draft.status ? 1 : 0) +
    (draft.type ? 1 : 0) +
    (draft.link ? 1 : 0) +
    (validityChanged ? 1 : 0) +
    flagChanges;
  const incompleteValidity = draft.vmode === "set" && !validityChanged;

  const apply = () => {
    const d0 = draft;
    const fields = {};
    if (d0.status) fields.documentStatus = d0.status;
    if (d0.type) {
      const i = d0.type.indexOf("|");
      fields.docGroup = d0.type.slice(0, i);
      fields.docType = d0.type.slice(i + 1);
    }
    if (validityChanged) {
      if (d0.start) fields.reportEffectiveDate = String(d0.start).slice(0, 10);
      if (Number(d0.days) > 0) fields.reportGuidelineExpirationDays = Number(d0.days);
    }
    Object.entries(d0.flags).forEach(([f, v]) => {
      fields[f] = v;
    });
    const target = findTarget(d0.link);
    onApply({
      metadata: Object.keys(fields).length ? () => ({ ...fields }) : null,
      link: target ? { target, mode: "add" } : null,
      count,
    });
  };


  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      // Above the floating loan-chat button (z 1250) so it never sits on top
      // of the panel; still below menus (1300) opened from inside it.
      sx={{ zIndex: 1260 }}
      PaperProps={{
        role: "dialog",
        "aria-modal": true,
        "aria-labelledby": "bulk-edit-all-title",
        sx: { width: { xs: "100%", sm: 460 }, display: "flex", flexDirection: "column" },
      }}
    >
      <Flx
        jb
        g={1.5}
        sx={{ ...formSx.sidebarHead, alignItems: "flex-start" }}
      >
        <Box>
          <Txt
            id="bulk-edit-all-title"
            component="h2"
            sx={{ ...formSx.title, fontSize: FORM.type.panelTitle[0], m: 0 }}
          >
            Edit {plural(n, "file")}
          </Txt>
          <Txt sx={formSx.titleHelper}>
            Only the fields you change are applied. Everything else stays as it is.
          </Txt>
        </Box>
        <IconButton aria-label="Close" onClick={onClose} size="small">
          <CloseRounded fontSize="small" />
        </IconButton>
      </Flx>

      <Box sx={{ flex: 1, overflow: "auto", py: "8px" }}>
        <Fld
          title="Status"
          now={nowStatus}
          changed={!!draft.status}
          onReset={() => set({ status: "" })}
        >
          <FormSelectField
            value={draft.status}
            onChange={(v) => set({ status: v || "" })}
            placeholder="Keep current"
            options={[
              { value: "", label: "Keep current" },
              ...STATUS_OPTIONS.map((s) => ({
                value: s.key,
                label: documentStatusLabel(s.key),
              })),
            ]}
          />
        </Fld>
        <Fld
          title="Type"
          now={nowType}
          changed={!!draft.type}
          onReset={() => set({ type: "" })}
        >
          <FormSelectField
            value={draft.type}
            onChange={(v) => set({ type: v || "" })}
            placeholder="Keep current"
            options={[{ value: "", label: "Keep current" }, ...typeOptions]}
          />
        </Fld>
        <Fld
          title="Add a link"
          now={linkedNow.length ? `Linked now: ${linkedNow.join(", ")}` : "No links yet"}
          changed={!!draft.link}
          onReset={() => set({ link: "" })}
        >
          <FormSelectField
            value={draft.link}
            onChange={(v) => set({ link: v || "" })}
            placeholder="Don't add a link"
            options={[{ value: "", label: "Don't add a link" }, ...linkOptions]}
          />
        </Fld>
        <Fld
          title="Validity"
          now={nowValidity}
          changed={validityChanged}
          onReset={() => set({ vmode: "keep", start: null, days: "" })}
        >
          <FormSegmented
            ariaLabel="Validity"
            value={draft.vmode}
            onChange={(v) => v && set({ vmode: v })}
            options={[
              { value: "keep", label: "Keep current" },
              { value: "set", label: "Set period", accent: true },
            ]}
          />
          {draft.vmode === "set" ? (
            <Box
              sx={{
                mt: "12px",
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: "10px",
              }}
            >
              <FormDateField
                label="Start date"
                value={draft.start}
                onChange={(v) => set({ start: v })}
              />
              <FormTextField
                label="Days valid"
                type="number"
                placeholder="e.g. 90"
                value={draft.days}
                onChange={(v) => set({ days: v })}
              />
            </Box>
          ) : null}
        </Fld>
        <Fld
          title="Sharing and visibility"
          changed={flagChanges > 0}
          onReset={() => set({ flags: {} })}
        >
          {EDIT_ALL_FLAGS.map((f, i) => {
            const on = docs.filter((d) => isFlagOn(d, f.field)).length;
            const v = draft.flags[f.field];
            return (
              <Flx
                key={f.field}
                ac
                jb
                g={1.5}
                sx={{
                  py: "8px",
                  borderTop: i ? `1px solid ${slate[200]}` : "none",
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Txt component="div" sx={formSx.checkRowTitle}>
                    {f.label}
                  </Txt>
                  <Txt component="div" sx={formSx.checkRowSub}>
                    {on === n ? "On for all" : on ? `On for ${on} of ${n}` : "Off for all"}
                  </Txt>
                </Box>
                <FormSegmented
                  ariaLabel={f.label}
                  value={v === undefined ? "keep" : v ? "on" : "off"}
                  onChange={(next) =>
                    setDraft((d) => {
                      const flags = { ...d.flags };
                      if (!next || next === "keep") delete flags[f.field];
                      else flags[f.field] = next === "on";
                      return { ...d, flags };
                    })
                  }
                  options={[
                    { value: "keep", label: "Keep" },
                    { value: "on", label: "On", accent: true },
                    { value: "off", label: "Off" },
                  ]}
                />
              </Flx>
            );
          })}
        </Fld>
      </Box>

      <Flx
        ac
        g={1.25}
        sx={{ px: "22px", py: "14px", borderTop: `1px solid ${FORM.color.divider}` }}
      >
        <Txt sx={{ ...formSx.footStatus, minWidth: 0 }}>
          {incompleteValidity
            ? "Add a start date or number of days"
            : count
              ? `${plural(count, "change")} ready`
              : "No changes yet"}
        </Txt>
        <Button variant="text" color="gray" onClick={onClose} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={busy || !count || incompleteValidity}
          onClick={apply}
          sx={{ textTransform: "none" }}
        >
          {count && !incompleteValidity ? `Apply to ${plural(n, "file")}` : "Apply"}
        </Button>
      </Flx>
    </Drawer>
  );
};
EditAllDrawer.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  docs: PropTypes.array,
  catalog: PropTypes.object,
  targets: PropTypes.object,
  busy: PropTypes.bool,
  onApply: PropTypes.func,
};

export default BulkSelectionBar;
